import { Injectable } from '@angular/core';
import { BaseApiHandleOptions, BaseApiService } from './base-api.service';
import { Observable } from 'rxjs';
import {
    IcpCompanyDto,
    IcpMigrationStatusResponseDto,
    IcpFilterResponse,
    IcpLastGeneratedDateDto,
    ExportErrorsResponseApiDto,
    AutomationErrorsResponseApiDto,
    RunIntentAutomationDto,
    IcpCompanyReportResponseInterface,
    IcpContactReportResponseInterface,
    MappingFieldsRequestParamDto,
} from '../../dto';
import { CRMCurrentlyMappedFieldsResponse } from '../../dto';
import { IcpProviderEnum } from '../../enums';
import { SavedICPAutomationInterface } from 'src/app/modals/icp-automations/models';

@Injectable({
    providedIn: 'root'
})
export class IcpApiService {
    private readonly url: string = 'service/contactcloud/icp';

    constructor(private readonly baseApiService: BaseApiService) {}

    getMigrationStatus(uuId: string): Observable<IcpMigrationStatusResponseDto> {
        return this.baseApiService.get(`${this.url}/migration/get-status`, {
            params: { uuId },
            showError: true,
        });
    }

    getScript(accountId: number): Observable<{ script: string }> {
        return this.baseApiService.get(`${this.url}/script/get`, {
            params: { accountId },
            showError: true,
        });
    }

    startMigration(uuId: string, options?: BaseApiHandleOptions): Observable<null> {
        return this.baseApiService.post(`${this.url}/migration/start`, null, { ...options, params: { uuId } });
    }

    getFilterByProvider(request: any, provider: IcpProviderEnum = IcpProviderEnum.BOMBORA): Observable<IcpFilterResponse> {
        return this.baseApiService.post(
            `${this.url}/filter`,
            request,
            { params: { provider } }
        );
    }

    getCompanies(request: any, provider: IcpProviderEnum = IcpProviderEnum.BOMBORA): Observable<IcpCompanyDto[]> {
        return this.baseApiService.post(
            `${this.url}/companies`,
            request,
            { params: { provider } }
        );
    }

    getIcpExport(request: any, provider: IcpProviderEnum = IcpProviderEnum.BOMBORA): Observable<any> {
        return this.baseApiService.post(
            `${this.url}/export`,
            request,
            { params: { provider } },
            {
                responseType: 'text',
                observe: 'response',
            });
    }

    getIcpLastGeneratedDate(uuId: string): Observable<IcpLastGeneratedDateDto> {
        return this.baseApiService.get(`${this.url}/last-generated-date/${uuId}`, {
            showError: true,
        });
    }

    saveAutomation(newIntentObj: SavedICPAutomationInterface) {
        return this.baseApiService.post(`${this.url}/automations`, newIntentObj);
    }

    getAutomations(params: any = {}): Observable<SavedICPAutomationInterface[]> {
        return this.baseApiService.get(`${this.url}/automations`, { params });
    }

    deleteAutomation(automationId: number): Observable<void> {
        return this.baseApiService.delete(`${this.url}/automations/${automationId}`);
    }

    updateAutomation(automationId: number, updatedAutomation: Partial<SavedICPAutomationInterface>): Observable<void> {
        return this.baseApiService.patch(`${this.url}/automations/${automationId}`, updatedAutomation);
    }

    getCRMMappingFields(crm: string, params: MappingFieldsRequestParamDto = {}): Observable<any> {
        const requestParams = params.isAutomation ? { is_automation: true } : {};
        return this.baseApiService.get(`service/contactcloud/${crm.toLowerCase()}/fields`, { params: requestParams });
    }

    getExportErrors(): Observable<ExportErrorsResponseApiDto | AutomationErrorsResponseApiDto[]> {
        return this.baseApiService.get<ExportErrorsResponseApiDto | AutomationErrorsResponseApiDto[]>(
            `${this.url}/automations/export/errors`
        );
    }

    getCurrentlyMappedFields(crm: string): Observable<CRMCurrentlyMappedFieldsResponse> {
        return this.baseApiService.get(
          `service/contactcloud/crms/mappedFields`,
          {
            params: {
              crmType: crm.toUpperCase(),
              objectType: 'COMPANY'
            }
          }
        );
    }

    getCompanyIdsForReport(icpReportId: string): Observable<IcpCompanyReportResponseInterface> {
        return this.baseApiService.get<IcpCompanyReportResponseInterface>(`service/contactcloud/icp/automations/report/${icpReportId}?report_type=COMPANY`);
    }

    getContactIdsForReport(icpReportId: string): Observable<IcpContactReportResponseInterface> {
        return this.baseApiService.get<IcpContactReportResponseInterface>(`service/contactcloud/icp/automations/report/${icpReportId}?report_type=CONTACT`);
    }

    runIntentAutomation(viObj: RunIntentAutomationDto): Observable<null> {
        return this.baseApiService.post(`service/contactcloud/icp/automations/run`, viObj, { showError: true });
    }
}
